import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components";
import { useAuth } from "@/features/auth/context/AuthContext";
import { integrationService } from "@/features/integration/services/integration.service";
import type {
  IntegrationListItem,
  IntegrationInitResponse,
} from "@/features/integration/interfaces";
import toast from "react-hot-toast";
import {
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Share2,
  Loader2,
  CheckCircle2,
  Link2,
} from "lucide-react";

const OAUTH_COMPANY_ID_KEY = "oauth_company_id";
const OAUTH_PLATFORM_KEY = "oauth_platform";
const TWITTER_OAUTH_TOKEN_KEY = "twitter_oauth_token";
const INTEGRATIONS_QUERY_KEY = ["integrations"];

// OAuth in a new tab; callback gets session via postMessage (token + companyId + platform) when popup storage is partitioned

type PlatformKey =
  | "instagram"
  | "facebook"
  | "tiktok"
  | "youtube"
  | "linkedin"
  | "twitter";

const PLATFORMS: {
  key: PlatformKey;
  label: string;
  icon: React.ElementType;
  description: string;
}[] = [
  {
    key: "instagram",
    label: "Instagram",
    icon: Instagram,
    description: "Connect your Instagram business or creator account",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: Facebook,
    description: "Connect Facebook pages for publishing",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: Share2,
    description: "Connect your TikTok account",
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: Youtube,
    description: "Connect your YouTube channel",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: Linkedin,
    description: "Connect your LinkedIn profile",
  },
  {
    key: "twitter",
    label: "Twitter / X",
    icon: Share2,
    description: "Connect your Twitter account (OAuth 1.0a)",
  },
];

/** Display label for a list item using accountInfo */
function getConnectionLabel(item: IntegrationListItem): string {
  const a = item.accountInfo;
  if (!a) return item.platform;

  if (a.username) return `@${a.username}`;
  if (a.pageName) return a.pageName;
  if (a.displayName) return a.displayName;
  if (a.channelTitle) return a.channelTitle;
  if (a.customUrl) return a.customUrl;
  if (a.name) return a.name;

  return item.platform;
}

export const Platforms: React.FC = () => {
  const { user } = useAuth();
  const companyId = user?.companyId ?? null;
  const queryClient = useQueryClient();

  // Callback URL is set after mount so it's never blank (window is available)

  const {
    data: connections = [],
    isLoading: isLoadingList,
    error: listError,
  } = useQuery<IntegrationListItem[]>({
    queryKey: INTEGRATIONS_QUERY_KEY,
    queryFn: () => integrationService.getAllIntegrations(),
  });

  const [connecting, setConnecting] = useState<PlatformKey | null>(null);
  const [popupRef, setPopupRef] = useState<Window | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // When popup asks for session, send token + companyId + platform (popup has its own sessionStorage)
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data.type !== "string") return;

      if (data.type === "oauth-callback-ready") {
        const accessToken = localStorage.getItem("accessToken");
        const refreshToken = localStorage.getItem("refreshToken");
        const target = event.source as Window | null;
        if (accessToken && target?.postMessage && companyId != null && connecting) {
          target.postMessage(
            {
              type: "oauth-callback-token",
              accessToken,
              refreshToken,
              companyId,
              platform: connecting,
            },
            { targetOrigin: window.location.origin }
          );
        }
        return;
      }

      if (data.type !== "oauth-complete") return;

      setConnecting(null);
      if (popupRef && !popupRef.closed) {
        popupRef.close();
      }
      setPopupRef(null);

      if (data.success && data.integration) {
        queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
        toast.success(`${data.platform ?? "Platform"} connected successfully`);
      } else {
        toast.error(data.error ?? "Connection failed");
      }
    },
    [companyId, connecting, popupRef, queryClient]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (popupRef?.closed) {
        setConnecting(null);
        setPopupRef(null);
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [popupRef]);

  // Success toast when landing with ?connected=1 (e.g. same-tab redirect or manual)
  useEffect(() => {
    const connected = searchParams.get("connected");
    const platform = searchParams.get("platform");
    if (connected === "1") {
      queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      toast.success(
        platform
          ? `${platform} connected successfully`
          : "Platform connected successfully"
      );
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("connected");
          next.delete("platform");
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams, queryClient]);

  const initAndOpen = async (platform: PlatformKey) => {
    if (!companyId) {
      toast.error("You must be signed in with a company to connect platforms.");
      return;
    }

    setConnecting(platform);
    sessionStorage.setItem(OAUTH_COMPANY_ID_KEY, String(companyId));
    sessionStorage.setItem(OAUTH_PLATFORM_KEY, platform);

    try {
      let res!: IntegrationInitResponse;

      switch (platform) {
        case "instagram":
          res = await integrationService.initInstagram({ companyId });
          break;
        case "facebook":
          res = await integrationService.initFacebook({ companyId });
          break;
        case "tiktok":
          res = await integrationService.initTiktok({ companyId });
          break;
        case "youtube":
          res = await integrationService.initYoutube({ companyId });
          break;
        case "linkedin":
          res = await integrationService.initLinkedin({ companyId });
          break;
        case "twitter": {
          res = await integrationService.initTwitter({ companyId });
          if (res.oauthToken) {
            sessionStorage.setItem(TWITTER_OAUTH_TOKEN_KEY, res.oauthToken);
          }
          break;
        }
        default:
          setConnecting(null);
          return;
      }

      const win = window.open(res.authUrl, "_blank");
      if (win) {
        setPopupRef(win);
      } else {
        setConnecting(null);
        toast.error(
          "Opening was blocked. Please allow popups for this site and try again."
        );
      }
    } catch (err: unknown) {
      setConnecting(null);
      const message =
        err instanceof Error ? err.message : "Failed to start connection";
      toast.error(message);
    }
  };

  return (
    <div className="py-4 sm:py-6 lg:py-8 px-4 sm:px-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Platforms</h1>
        <p className="text-muted-foreground">
          Connect your social accounts to publish and manage content. Each
          connection opens in a new tab—complete the authorization there; when
          it finishes, close the tab and return here to see your connections.
        </p>
      </div>

      {!companyId && (
        <div className="mb-6 p-4 rounded-lg bg-muted border border-input text-sm text-muted-foreground">
          You need to be signed in with a company to connect platforms.
        </div>
      )}

      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          Your connections
        </h2>
        {isLoadingList ? (
          <div className="border border-input rounded-lg p-8 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading connections…
          </div>
        ) : listError ? (
          <div className="border border-input rounded-lg p-6 text-destructive text-sm">
            Failed to load connections. Please try again.
          </div>
        ) : connections.length === 0 ? (
          <div className="border border-dashed border-input rounded-lg p-8 text-center text-muted-foreground">
            <p className="mb-1">No connections yet</p>
            <p className="text-sm">
              Use the buttons below to connect a platform. New connections will
              appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {connections.map((conn: IntegrationListItem) => (
              <li
                key={conn.id}
                className="border border-input rounded-lg p-4 bg-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-medium shrink-0">{conn.platform}</span>
                  <span className="text-sm text-muted-foreground truncate">
                    {getConnectionLabel(conn)}
                  </span>
                  {conn.accountInfo?.accountType && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {conn.accountInfo.accountType}
                    </span>
                  )}
                  {conn.accountInfo?.type && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {conn.accountInfo.type}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  Connected
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-4">Connect a platform</h2>
        <ul className="grid gap-4 sm:grid-cols-2">
          {PLATFORMS.map(({ key, label, icon: Icon, description }) => (
            <li
              key={key}
              className="border border-input rounded-lg p-4 bg-card flex flex-col"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 rounded-lg bg-muted">
                  <Icon className="h-5 w-5 text-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium">{label}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {description}
                  </p>
                </div>
              </div>
              <Button
                className="mt-auto"
                variant="outline"
                size="sm"
                onClick={() => initAndOpen(key)}
                disabled={!companyId || connecting !== null}
              >
                {connecting === key ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Connecting…
                  </>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect {label}
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
